import type { RouteConfig } from "@asteasolutions/zod-to-openapi";
import {
  defaultErrorResponses,
  problemResponse,
  registry,
  undeclaredQueryRefused,
} from "../registry.js";
import { z } from "../zod.js";
import {
  AgentAction,
  AgentActionDecisionRequest,
  AgentActionListQuery,
  AgentActionListResponse,
  AgentActionUndoResponse,
  AgentDetail,
  AgentGuardrailListQuery,
  AgentGuardrailListResponse,
  AgentListResponse,
  AgentPatchRequest,
  AgentPolicyListResponse,
  AgentPolicyPutRequest,
  AgentPolicyRow,
  AgentPromptActivateResponse,
  AgentPromptVersion,
  AgentPromptVersionCreateRequest,
  AgentPromptVersionListResponse,
  AgentRun,
  AgentRunDetail,
  AgentRunListQuery,
  AgentRunListResponse,
  AgentScopeBindingsRequest,
  AgentScopeBindingsResponse,
  AgentSettings,
  AgentSettingsPatchRequest,
  AgentToolListQuery,
  AgentToolListResponse,
  AgentTriggerRequest,
  AgentTriggerResponse,
} from "./schemas.js";

// Module 14's HTTP surface in slice 14-A (spec 14 §5; D-512 ruling 2): every
// §5 row except the agent-session rows, the run stream, `GET
// /v1/agents/usage` and the `/v1/admin/agent-*` rows. Tagged `agents`.
// Refusals are listed in the order the door meets them. Every mutation takes
// an `Idempotency-Key`; lists are cursor-paginated (25, at most 100). Every
// `/v1/agent*` route is in `sales-value-matrix.int.test.ts`'s ROUTES with a
// driver (D-506 ruling 8). Registered with the platform contract commit of
// P3-K K-2 (WP-E0), before the controllers, so the web lane builds against
// the generated client; the controllers land in the same PR.

const sessionSecurity = [{ sessionCookie: [] }];
const idempotencyHeader = { $ref: "#/components/parameters/IdempotencyKey" };

function route(config: {
  method: "get" | "post" | "patch" | "put" | "delete";
  path: string;
  operationId: string;
  summary: string;
  description: string;
  request?: RouteConfig["request"];
  responses: RouteConfig["responses"];
}): void {
  registry.registerPath({
    method: config.method,
    path: config.path,
    operationId: config.operationId,
    tags: ["agents"],
    summary: config.summary,
    description: config.description,
    security: sessionSecurity,
    ...(config.request !== undefined ? { request: config.request } : {}),
    ...(config.method !== "get" ? { parameters: [idempotencyHeader] } : {}),
    responses: { ...defaultErrorResponses(), ...config.responses },
  });
}

const json = <S extends z.ZodType>(description: string, schema: S) => ({
  description,
  content: { "application/json": { schema } },
});

const IdParam = z.object({ id: z.guid() });
const ToolParam = z.object({
  id: z.guid(),
  toolCode: z.string().regex(/^[a-z]+\.[a-z0-9_]+$/),
});
const VersionParam = z.object({ id: z.guid(), versionId: z.guid() });

const VALUE_FENCE =
  "Money is served under the value grants and roles of every tool it comes from (spec 14 §7.5, D-535 ruling 1) and is omitted, never nulled, for any other reader.";

// --- agents -------------------------------------------------------------------

route({
  method: "get",
  path: "/v1/agents",
  operationId: "listAgents",
  summary: "The tenant's agents (the Agent Hub)",
  description:
    "Spec 14 §4.2 and §5. `agents.read`; a holder of `agents.invoke` without it reads the agents of its own domain only, read-only (D-512 ruling 8). `usage_mtd.month` is the tenant-zone month. `usage_mtd.spend_minor` and `spend_currency` are served only to a reader holding every contributing tool's value grants (D-512 ruling 4). `tools_registered` false means the agent's tool list names a tool this build has not registered, and its enable control is not rendered (D-512 ruling 3). " +
    VALUE_FENCE,
  responses: {
    200: json("The agents the caller may see", AgentListResponse),
    400: undeclaredQueryRefused,
  },
});

route({
  method: "get",
  path: "/v1/agents/settings",
  operationId: "getAgentSettings",
  summary: "The tenant's agent settings",
  description:
    "Spec 14 §2.12. `agents.read`. The facility policy money ceiling: the highest `policy_params.cap_minor` a holder of `agents.manage` without `agents.config.manage` may set on a money tool; 0 at launch (D-507 ruling 9).",
  responses: {
    200: json("The settings row", AgentSettings),
    400: undeclaredQueryRefused,
  },
});

route({
  method: "patch",
  path: "/v1/agents/settings",
  operationId: "patchAgentSettings",
  summary: "Change the tenant's agent settings",
  description:
    "Spec 14 §2.12 and §5. `agents.config.manage`. In order: 403 → 400. Appends `agent_settings.updated` to the audit chain.",
  request: { body: { content: { "application/json": { schema: AgentSettingsPatchRequest } } } },
  responses: {
    200: json("The settings row", AgentSettings),
  },
});

route({
  method: "get",
  path: "/v1/agents/tools",
  operationId: "listAgentTools",
  summary: "The visible tool registry",
  description:
    "Spec 14 §2.1 and §5. `agents.read`. Served from the in-memory boot registry, filtered by the tenant's enabled modules, and never from Postgres. A tool's name is the i18n key `agents.tools.<tool_code>.name`.",
  request: { query: AgentToolListQuery },
  responses: {
    200: json("The tools", AgentToolListResponse),
    400: undeclaredQueryRefused,
  },
});

route({
  method: "get",
  path: "/v1/agents/{id}",
  operationId: "getAgent",
  summary: "One agent with its memberships and active prompt",
  description:
    "Spec 14 §4.3 and §5. `agents.read`, or `agents.invoke` for an agent of the caller's own domain. Memberships are read from module 16. 404 `AGENT_NOT_FOUND` for an unknown agent or one outside the caller's reach. " +
    VALUE_FENCE,
  request: { params: IdParam },
  responses: {
    200: json("The agent", AgentDetail),
    400: undeclaredQueryRefused,
    404: problemResponse("AGENT_NOT_FOUND"),
  },
});

route({
  method: "patch",
  path: "/v1/agents/{id}",
  operationId: "patchAgent",
  summary: "Change an agent's status, name, caps or model profile",
  description:
    "Spec 14 §5. Status and display name need `agents.manage` within the agent's scope; caps and `model_profile` need `agents.config.manage`. In order: 403 → 404 `AGENT_NOT_FOUND` → 400 → 409 `AGENT_VERSION_CONFLICT` → 422 `AGENT_PROFILE_UNKNOWN` → 422 `AGENT_TOOLS_MISSING` when enabling an agent whose tools are not all registered, naming the tools and modules (D-512 ruling 3). Appends `agent.updated`.",
  request: {
    params: IdParam,
    body: { content: { "application/json": { schema: AgentPatchRequest } } },
  },
  responses: {
    200: json("The agent", AgentDetail),
    404: problemResponse("AGENT_NOT_FOUND"),
    409: problemResponse("AGENT_VERSION_CONFLICT"),
    422: problemResponse("AGENT_PROFILE_UNKNOWN / AGENT_TOOLS_MISSING"),
  },
});

route({
  method: "put",
  path: "/v1/agents/{id}/scope-bindings",
  operationId: "putAgentScopeBindings",
  summary: "Replace an agent's memberships",
  description:
    "Spec 14 §2.3 and §5, through module 16's membership service in one transaction. `agents.manage`, adding or removing only memberships within the caller's own `agents.manage` scope. In order: 403 → 404 `AGENT_NOT_FOUND` → 400 → 403 `AGENT_BINDING_OUTSIDE_SCOPE` (D-512 ruling 10).",
  request: {
    params: IdParam,
    body: { content: { "application/json": { schema: AgentScopeBindingsRequest } } },
  },
  responses: {
    200: json("The agent's memberships", AgentScopeBindingsResponse),
    403: problemResponse("PERMISSION_DENIED / AGENT_BINDING_OUTSIDE_SCOPE"),
    404: problemResponse("AGENT_NOT_FOUND"),
  },
});

// --- policies -------------------------------------------------------------------

route({
  method: "get",
  path: "/v1/agents/{id}/policies",
  operationId: "listAgentPolicies",
  summary: "The effective policy of each tool on the agent's list",
  description:
    "Spec 14 §2.4 and §5. `agents.read`. One item per registered tool on the agent's own list (D-509 ruling 11), with `source` `explicit` (a row) or `declared` (no row), plus every orphaned row, struck through on the screen and dismissable (D-512 ruling 7). " +
    VALUE_FENCE,
  request: { params: IdParam },
  responses: {
    200: json("The policies", AgentPolicyListResponse),
    400: undeclaredQueryRefused,
    404: problemResponse("AGENT_NOT_FOUND"),
  },
});

route({
  method: "put",
  path: "/v1/agents/{id}/policies/{toolCode}",
  operationId: "putAgentPolicy",
  summary: "Set one tool's policy for an agent",
  description:
    "Spec 14 §2.4 and §5 (D-509 ruling 1). `agents.manage`, with §2.4's rules. In order: 403 → 404 `AGENT_NOT_FOUND` → 404 `AGENT_TOOL_NOT_FOUND` → 400 (`policy_params` against the tool's own schema) → 422 `AGENT_AUTONOMY_NOT_ALLOWED` (laxer than `maxAutonomy`) → 422 `AGENT_IRREVERSIBLE_NOT_AUTONOMOUS` → 403 `AGENT_POLICY_LAXER_REQUIRES_CONFIG` → 403 `AGENT_FACILITY_CEILING_EXCEEDED` → 409 `AGENT_POLICY_VERSION_CONFLICT`. Appends `agent.policy_set`.",
  request: {
    params: ToolParam,
    body: { content: { "application/json": { schema: AgentPolicyPutRequest } } },
  },
  responses: {
    200: json("The policy row", AgentPolicyRow),
    403: problemResponse(
      "PERMISSION_DENIED / AGENT_POLICY_LAXER_REQUIRES_CONFIG / AGENT_FACILITY_CEILING_EXCEEDED",
    ),
    404: problemResponse("AGENT_NOT_FOUND / AGENT_TOOL_NOT_FOUND"),
    409: problemResponse("AGENT_POLICY_VERSION_CONFLICT"),
    422: problemResponse("AGENT_AUTONOMY_NOT_ALLOWED / AGENT_IRREVERSIBLE_NOT_AUTONOMOUS"),
  },
});

route({
  method: "delete",
  path: "/v1/agents/{id}/policies/{toolCode}",
  operationId: "dismissAgentPolicy",
  summary: "Dismiss an orphaned policy row",
  description:
    "Spec 14 §2.4 and §5 (D-512 ruling 7). `agents.manage`. Only an orphan may be dismissed: a row whose tool is deprecated or absent from the boot registry and is not on the agent's own list. In order: 403 → 404 → 409 `AGENT_POLICY_NOT_ORPHANED`. Appends `agent.policy_dismissed`.",
  request: { params: ToolParam },
  responses: {
    204: { description: "Dismissed" },
    404: problemResponse("AGENT_NOT_FOUND / AGENT_TOOL_NOT_FOUND"),
    409: problemResponse("AGENT_POLICY_NOT_ORPHANED"),
  },
});

// --- prompt versions -------------------------------------------------------------

route({
  method: "get",
  path: "/v1/agents/{id}/prompt-versions",
  operationId: "listAgentPromptVersions",
  summary: "An agent's prompt history",
  description:
    "Spec 14 §2.5 and §4.3's prompt tab (D-512 ruling 6, D-536). `agents.read`. Every version of the agent's prompt, newest first, by source: `launch_pack`, `platform_update` (an inactive one waits for a group admin to adopt it) and `tenant_override`.",
  request: { params: IdParam },
  responses: {
    200: json("The versions", AgentPromptVersionListResponse),
    400: undeclaredQueryRefused,
    404: problemResponse("AGENT_NOT_FOUND"),
  },
});

route({
  method: "post",
  path: "/v1/agents/{id}/prompt-versions",
  operationId: "createAgentPromptVersion",
  summary: "Create a tenant override of an agent's prompt",
  description:
    "Spec 14 §2.5 and §5. `agents.config.manage`. The version is created inactive; activating it is a separate call. In order: 403 → 404 `AGENT_NOT_FOUND` → 400. Appends `agent_prompt_version.created`.",
  request: {
    params: IdParam,
    body: { content: { "application/json": { schema: AgentPromptVersionCreateRequest } } },
  },
  responses: {
    201: json("The new version, inactive", AgentPromptVersion),
    404: problemResponse("AGENT_NOT_FOUND"),
  },
});

route({
  method: "post",
  path: "/v1/agents/{id}/prompt-versions/{versionId}/activate",
  operationId: "activateAgentPromptVersion",
  summary: "Activate one of an agent's prompt versions",
  description:
    "Spec 14 §2.5 and §5. `agents.config.manage`. Activates a `tenant_override` or an inactive `platform_update`; exactly one version is active per agent. In order: 403 → 404 `AGENT_NOT_FOUND` / `AGENT_PROMPT_VERSION_NOT_FOUND` → 409 `AGENT_PROMPT_ACTIVATION_CONFLICT` for a concurrent activation. Appends `agent_prompt_version.activated`.",
  request: { params: VersionParam },
  responses: {
    200: json("Activated", AgentPromptActivateResponse),
    404: problemResponse("AGENT_NOT_FOUND / AGENT_PROMPT_VERSION_NOT_FOUND"),
    409: problemResponse("AGENT_PROMPT_ACTIVATION_CONFLICT"),
  },
});

// --- triggers and runs -----------------------------------------------------------

route({
  method: "post",
  path: "/v1/agents/{id}/trigger",
  operationId: "triggerAgent",
  summary: "Run an agent now (Draft offer)",
  description:
    "Spec 14 §5 (D-515 ruling 4, D-519 ruling 5, D-530 ruling 2). `agents.invoke` for an agent of the caller's own domain; `agents.manage` for any agent in scope. The server stamps `trigger_ref.requested_by` with the caller. In order: 403 → 404 `AGENT_NOT_FOUND` → 400 `AGENT_TRIGGER_INVALID` → 404 `AGENT_RUN_SUBJECT_NOT_FOUND`, answered by the agent's trigger authorizer under the CALLER's context, so a reader cannot reach a customer through the agent that they cannot see, and answered for every trigger by an agent with no authorizer bound → 409 `AGENT_PAUSED` → 429 `AGENT_TOKEN_BUDGET_EXHAUSTED` → 429 `AGENT_PLATFORM_TOKEN_CEILING_REACHED`.",
  request: {
    params: IdParam,
    body: { content: { "application/json": { schema: AgentTriggerRequest } } },
  },
  responses: {
    201: json("The queued run", AgentTriggerResponse),
    400: problemResponse("VALIDATION_FAILED / AGENT_TRIGGER_INVALID / IDEMPOTENCY_KEY_REQUIRED"),
    404: problemResponse("AGENT_NOT_FOUND / AGENT_RUN_SUBJECT_NOT_FOUND"),
    409: problemResponse("AGENT_PAUSED"),
    429: problemResponse("AGENT_TOKEN_BUDGET_EXHAUSTED / AGENT_PLATFORM_TOKEN_CEILING_REACHED"),
  },
});

route({
  method: "get",
  path: "/v1/agent-runs",
  operationId: "listAgentRuns",
  summary: "Agent runs",
  description:
    "Spec 14 §5 (D-519 ruling 5). `agents.activity.read`; a holder of `agents.invoke` reads the runs of its own domain's agent, narrowed to the subjects it reaches through the subject kind's reach binding, and a kind with no binding narrows to nothing (never a refusal, D-224 ruling 5).",
  request: { query: AgentRunListQuery },
  responses: {
    200: json("The runs", AgentRunListResponse),
    400: undeclaredQueryRefused,
  },
});

route({
  method: "get",
  path: "/v1/agent-runs/{id}",
  operationId: "getAgentRun",
  summary: "One run with its guardrail events and action ids",
  description:
    "Spec 14 §5 (D-512 ruling 2). As the list. A run outside the reader's reach is 404 `AGENT_RUN_NOT_FOUND`. The ordered timeline arrives with 14-B.",
  request: { params: IdParam },
  responses: {
    200: json("The run", AgentRunDetail),
    400: undeclaredQueryRefused,
    404: problemResponse("AGENT_RUN_NOT_FOUND"),
  },
});

route({
  method: "post",
  path: "/v1/agent-runs/{id}/cancel",
  operationId: "cancelAgentRun",
  summary: "Cancel a queued or running run",
  description:
    "Spec 14 §3.4 and §5 (D-509 ruling 12). `agents.manage` within the agent's scope for `queued` or `running`; the caller stamped as `trigger_ref.requested_by` for its own `queued` manual run. A running run stops at its next fenced write; an execution already in flight finishes and is journaled. In order: 403 → 404 `AGENT_RUN_NOT_FOUND` → 409 `AGENT_RUN_NOT_CANCELLABLE`. Appends `agent_run.cancelled`.",
  request: { params: IdParam },
  responses: {
    200: json("The run", AgentRun),
    404: problemResponse("AGENT_RUN_NOT_FOUND"),
    409: problemResponse("AGENT_RUN_NOT_CANCELLABLE"),
  },
});

route({
  method: "get",
  path: "/v1/agent-guardrail-events",
  operationId: "listAgentGuardrailEvents",
  summary: "Guardrail events",
  description:
    "Spec 14 §2.11 and §5. `agents.activity.read`, scoped to the agents bound within the reader's memberships. `detail` is typed per kind (D-506 ruling 8). " +
    VALUE_FENCE,
  request: { query: AgentGuardrailListQuery },
  responses: {
    200: json("The events", AgentGuardrailListResponse),
    400: undeclaredQueryRefused,
  },
});

// --- the action journal ----------------------------------------------------------

route({
  method: "get",
  path: "/v1/agent-actions",
  operationId: "listAgentActions",
  summary: "The action journal",
  description:
    "Spec 14 §4.4 and §5. Session-scoped: the rows the reader passes §7.2's checks 1 and 3 for. `impact_min_minor` without the value grants it filters on is refused 403 `AGENT_IMPACT_FILTER_REQUIRES_VALUE`, never silently dropped. Each card carries `can_approve`, `can_undo`, `undo_mode` and `compensation_key` for the caller, and its `evidence` typed per tool (D-506 ruling 8). " +
    VALUE_FENCE,
  request: { query: AgentActionListQuery },
  responses: {
    200: json("The cards", AgentActionListResponse),
    400: undeclaredQueryRefused,
    403: problemResponse("AGENT_IMPACT_FILTER_REQUIRES_VALUE"),
  },
});

route({
  method: "get",
  path: "/v1/agent-actions/{id}",
  operationId: "getAgentAction",
  summary: "One action card",
  description:
    "Spec 14 §5. As the journal; an action the reader may not see is 404 `AGENT_ACTION_NOT_FOUND`. " +
    VALUE_FENCE,
  request: { params: IdParam },
  responses: {
    200: json("The card", AgentAction),
    400: undeclaredQueryRefused,
    404: problemResponse("AGENT_ACTION_NOT_FOUND"),
  },
});

const DECISION_ORDER =
  "In order: 404 `AGENT_ACTION_NOT_FOUND` (§7.2 check 3, or no such action) → 403 `AGENT_APPROVAL_REQUIRES_FIELD_VISIBILITY` (check 2) → 403 `PERMISSION_DENIED` (check 1) → 409 `AGENT_ACTION_EXPIRED` → 409 `AGENT_ACTION_ALREADY_DECIDED`. A replay is re-fenced for the replaying principal (D-510 ruling 6).";

route({
  method: "post",
  path: "/v1/agent-actions/{id}/approve",
  operationId: "approveAgentAction",
  summary: "Approve a proposed action",
  description:
    "Spec 14 §3.5, §5 and §7.2 (D-510 rulings 1 and 4). The approver passes §7.2's three checks; the action then executes under the agent's own principal with the approver chained into the envelope. The answer is the card `executed` when the handler finishes within 20 s, else `executing`, which the worker re-drives. " +
    DECISION_ORDER,
  request: {
    params: IdParam,
    body: { content: { "application/json": { schema: AgentActionDecisionRequest } } },
  },
  responses: {
    200: json("The card", AgentAction),
    403: problemResponse("AGENT_APPROVAL_REQUIRES_FIELD_VISIBILITY / PERMISSION_DENIED"),
    404: problemResponse("AGENT_ACTION_NOT_FOUND"),
    409: problemResponse("AGENT_ACTION_EXPIRED / AGENT_ACTION_ALREADY_DECIDED"),
  },
});

route({
  method: "post",
  path: "/v1/agent-actions/{id}/reject",
  operationId: "rejectAgentAction",
  summary: "Reject a proposed action",
  description:
    "Spec 14 §3.5, §5 and §7.2. The same three checks as approve, with an optional note. " +
    DECISION_ORDER,
  request: {
    params: IdParam,
    body: { content: { "application/json": { schema: AgentActionDecisionRequest } } },
  },
  responses: {
    200: json("The card", AgentAction),
    403: problemResponse("AGENT_APPROVAL_REQUIRES_FIELD_VISIBILITY / PERMISSION_DENIED"),
    404: problemResponse("AGENT_ACTION_NOT_FOUND"),
    409: problemResponse("AGENT_ACTION_EXPIRED / AGENT_ACTION_ALREADY_DECIDED"),
  },
});

route({
  method: "post",
  path: "/v1/agent-actions/{id}/undo",
  operationId: "undoAgentAction",
  summary: "Undo an executed action by its reversibility",
  description:
    "Spec 14 §3.5, §5 and §7.2 (D-510 ruling 2, D-511). §7.2's three checks as the caller, with no `agents.manage` or group_admin shortcut. A reversible action runs its reversal as a new pre-approved row; a compensatable one with a compensation tool drafts it `proposed`; one with none is 409. In order: 404 → 403 → 409 `AGENT_ACTION_NOT_UNDOABLE` (irreversible, compensatable with no compensation tool, or not `executed`) → 409 `AGENT_ACTION_ALREADY_DECIDED`. Replay-fenced as approve.",
  request: {
    params: IdParam,
    body: { content: { "application/json": { schema: AgentActionDecisionRequest } } },
  },
  responses: {
    200: json("The original and the compensating action", AgentActionUndoResponse),
    403: problemResponse("AGENT_APPROVAL_REQUIRES_FIELD_VISIBILITY / PERMISSION_DENIED"),
    404: problemResponse("AGENT_ACTION_NOT_FOUND"),
    409: problemResponse("AGENT_ACTION_NOT_UNDOABLE / AGENT_ACTION_ALREADY_DECIDED"),
  },
});
